import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthAPI } from "../services/api";
import type { UserCompany } from "../services/api";

const SuperAdminCompanies: React.FC = () => {
  const navigate = useNavigate();

  const [companies, setCompanies] = useState<UserCompany[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const loadCompanies = async () => {
      try {
        setLoading(true);

        const res = await AuthAPI.me();

        const allCompanies: UserCompany[] =
          res.data?.companies || [];

        // Only companies where the logged-in user
        // has Super Admin role
        const superAdminCompanies =
          allCompanies.filter(
            (company) =>
              company.userType?.toLowerCase() ===
              "superadmin"
          );

        setCompanies(superAdminCompanies);
      } catch (err) {
        console.error(
          "Failed to load companies:",
          err
        );

        setError(
          "Failed to load your companies."
        );
      } finally {
        setLoading(false);
      }
    };

    loadCompanies();
  }, []);

  // =========================================================
  // SELECT COMPANY
  // =========================================================
  const handleSelectCompany = (
    companyNo: number
  ) => {
    navigate("/add-user", {
      state: {
        companyNo,
      },
    });
  };

  // =========================================================
  // BACK
  // =========================================================
  const handleBack = () => {
    navigate("/");
  };

  // =========================================================
  // UI
  // =========================================================
  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-6">

      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl">

        {/* =====================================================
            HEADER
        ===================================================== */}
        <div className="flex justify-between items-center px-6 py-4 border-b">

          <div>
            <h1 className="text-xl font-semibold text-gray-800">
              Super Admin
            </h1>

            <p className="text-sm text-gray-500 mt-1">
              Select a company to open its dashboard
            </p>
          </div>

          <button
            onClick={handleBack}
            className="text-gray-500 hover:text-gray-800 text-xl"
          >
            ✕
          </button>

        </div>

        {/* =====================================================
            CONTENT
        ===================================================== */}
        <div className="p-6">

          {loading && (
            <div className="text-center py-10 text-gray-500">
              Loading companies...
            </div>
          )}

          {!loading && error && (
            <div className="text-center py-10 text-red-500">
              {error}
            </div>
          )}

          {!loading &&
            !error &&
            companies.length === 0 && (
              <div className="text-center py-10">

                <p className="text-gray-500">
                  You don't have Super Admin access
                  to any company.
                </p>

              </div>
            )}

          {/* =================================================
              COMPANY LIST
          ================================================= */}
          {!loading &&
            !error &&
            companies.length > 0 && (

              <div className="space-y-3">

                {companies.map((company) => (

                  <button
                    key={company.companyNo}
                    onClick={() =>
                      handleSelectCompany(
                        company.companyNo
                      )
                    }
                    className="w-full text-left border rounded-lg p-4 hover:border-blue-500 hover:bg-blue-50 transition flex items-center justify-between"
                  >

                    <div>

                      <div className="font-semibold text-gray-800">
                        {company.companyName}
                      </div>

                      <div className="text-sm text-gray-500 mt-1">
                        Company No:{" "}
                        {company.companyNo}
                      </div>

                    </div>

                    <div className="flex items-center gap-3">

                      <span className="px-3 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-700">
                        {company.userType}
                      </span>

                      <span className="text-gray-400 text-lg">
                        →
                      </span>

                    </div>

                  </button>

                ))}

              </div>
            )}

        </div>

        {/* =====================================================
            FOOTER
        ===================================================== */}
        <div className="border-t px-6 py-4">

          <button
            onClick={handleBack}
            className="px-4 py-2 border rounded text-gray-700 hover:bg-gray-100"
          >
            Back to Projects
          </button>

        </div>

      </div>

    </div>
  );
};

export default SuperAdminCompanies;